import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import {
  focusManager,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  useQuery,
} from '@tanstack/react-query'
import globalErrorHandler from '~/app/react-query/global/errorHandler'
import { UseInfiniteQueryOptions } from '@tanstack/react-query/src/types'
import { getYaNative } from '@yanolja-rnd/ya-native'
import * as Sentry from '@sentry/browser'
import { useEffect } from 'react'
import { isNative } from '~/app/utils/typeguards'
import domesticErrorHandler from '~/app/react-query/domestic/errorHandler'
import { IAxiosClientError } from '~/app/react-query/axios'
import { isAxiosError } from 'axios'

export const isAxiosClientError = (error: any): error is IAxiosClientError => {
  return isAxiosError(error)
}

/**
 *  @link https://yanolja.atlassian.net/wiki/spaces/FES/pages/192153257/react-query+refetchOnWindowFocus
 **/
const useVisibilityChangeFocusManager = () => {
  useEffect(() => {
    const yaNative = getYaNative()

    try {
      focusManager.setEventListener(handleFocus => {
        // App 환경에서만 활성화
        if (isNative(yaNative)) {
          // refetchOnWindowFocus: true 인 경우, focus 시에도 재호출이 발생하는데
          // 이는 웹뷰 환경에서 불필요한 재호출을 유발한다.
          window.addEventListener('visibilitychange', () => handleFocus(), false)

          return () => {
            window.removeEventListener('visibilitychange', () => handleFocus())
          }
        }
      })
    } catch (e) {
      Sentry.withScope(scope => {
        scope.setExtra('message', 'focusManager.setEventListener error')
        Sentry.captureException(e)
      })
    }
  }, [])
}

/**
 * 공통 useQuery
 *
 * - window 포커스 시 재호출 X
 * - retry X
 * - staleTime 5초로 설정하여 prefetch 후 fetch 하지 않도록 함.
 */
export const useReactQuery = <
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>({
  queryKey,
  queryFn,
  queryOptions = {},
  useCommonErrorHandler = true,
  target,
}: {
  queryKey: TQueryKey
  queryFn: QueryFunction<TQueryFnData, TQueryKey>
  queryOptions?: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'queryKey' | 'queryFn'
  >
  useCommonErrorHandler?: boolean
  target: 'global' | 'domestic' | 'common'
}): UseQueryResult<TData, TError> => {
  useVisibilityChangeFocusManager()

  const result = useQuery({
    queryKey,
    queryFn,
    retry: false,
    staleTime: 5 * 1000,
    ...queryOptions,
  })

  if (result.isError && isAxiosClientError(result.error) && useCommonErrorHandler) {
    target === 'global' ? globalErrorHandler(result.error) : domesticErrorHandler(result.error)
  }
  return result
}

/**
 * 공통 useMutation
 *
 * - retry 1회
 */
export const useReactMutation = <
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>({
  mutationFn,
  target,
  options,
}: {
  mutationFn: MutationFunction<TData, TVariables>
  target: 'global' | 'domestic' | 'common'
  options?: Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationKey'> & {
    useCommonErrorHandler?: boolean
  }
}): UseMutationResult<TData, TError, TVariables, TContext> => {
  const { useCommonErrorHandler = true, ...mutateOptions } = options ?? {}
  const result = useMutation({
    mutationFn,
    retry: 1,
    ...mutateOptions,
  })

  if (result.isError && isAxiosClientError(result.error) && useCommonErrorHandler) {
    target === 'global' ? globalErrorHandler(result.error) : domesticErrorHandler(result.error)
  }
  return result
}

/**
 * 공통 useQuery
 *
 * - window 포커스 시 재호출 X
 * - retry X
 * - staleTime 5초로 설정하여 prefetch 후 fetch 하지 않도록 함.
 */
export const useReactInfiniteQuery = <
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>({
  queryKey,
  queryFn,
  getNextPageParam,
  target,
  ...queryOptions
}: UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryFnData, TQueryKey> & {
  target: 'global' | 'domestic' | 'common'
}): UseInfiniteQueryResult<TData, TError> => {
  useVisibilityChangeFocusManager()

  const result = useInfiniteQuery({
    queryKey,
    queryFn,
    getNextPageParam,
    retry: false,
    staleTime: 5 * 1000,
    ...queryOptions,
  })

  if (result.isError && isAxiosClientError(result.error)) {
    target === 'global' ? globalErrorHandler(result.error) : domesticErrorHandler(result.error)
  }
  return result
}
